<script lang="ts">
export type PageHeaderReadMore = {
  text: string;
  href: string;
};
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { FunctionalComponent } from 'vue';
import { RouterLink } from 'vue-router';
import * as LucideIcons from 'lucide-vue-next';
import { useIndmoRichTextIcons } from '@/composables/useIndmoRichTextIcons';
import { buildSrcSet, isOptimisedImage } from '@/types/images';
import type { ApiImage } from '@/types/images';
import { wagtailRichTitleInnerHtmlForH1 } from '@/utils/html';
import { offlineMediaPlaceholderUrl } from '@/utils/offlineMediaPlaceholder';

const ArrowRight = (LucideIcons as Record<string, FunctionalComponent>).ArrowRight;

const props = defineProps<{
  /** `featured` = full-bleed home band (HomePage only); `minimal` = text-first header. */
  variant: 'minimal' | 'featured';
  plainTitle: string;
  titleHtml?: string | null;
  description?: string | null;
  image?: ApiImage | null;
  /** Alt text for the promote image (usually from page title / image title). */
  imageAlt?: string;
  readMore?: PageHeaderReadMore | null;
}>();

const hasTitleHtml = computed(
  () => typeof props.titleHtml === 'string' && props.titleHtml.trim().length > 0
);

/** Inner HTML for `<h1>` — unwraps Wagtail’s default `<p>` wrapper so the heading is a real h1. */
const richTitleHeadingInnerHtml = computed(() => {
  const raw = props.titleHtml;
  if (typeof raw !== 'string' || !raw.trim()) return '';
  return wagtailRichTitleInnerHtmlForH1(raw);
});

const hasPageImage = computed(() => {
  const img = props.image;
  if (!img) return false;
  if (isOptimisedImage(img)) return !!(img.fallback?.url);
  return !!(img && 'url' in img && (img as { url?: string }).url);
});

const showFeaturedBand = computed(
  () => props.variant === 'featured' && hasPageImage.value
);

const pageImage = computed(() => props.image ?? null);

const pageImageUrl = computed(() => {
  const img = pageImage.value;
  if (!img) return null;
  if (isOptimisedImage(img)) return img.fallback?.url ?? null;
  return 'url' in img ? (img as { url?: string }).url ?? null : null;
});

const resolvedImageAlt = computed(() => props.imageAlt ?? props.plainTitle);

/** Set when promote `<img>` fires `error` (uncached / offline miss). Cached images still load offline — do not gate on `navigator.onLine`. */
const featuredHeroMediaBroken = ref(false);

watch(
  () => props.image,
  () => {
    featuredHeroMediaBroken.value = false;
  }
);

function onFeaturedHeroMediaError() {
  featuredHeroMediaBroken.value = true;
}

const richTitleHeadingEl = ref<HTMLElement | null>(null);
useIndmoRichTextIcons(richTitleHeadingEl, () => richTitleHeadingInnerHtml.value);

function isExternalOrHash(href: string): boolean {
  return (
    href.startsWith('#') ||
    href.startsWith('http://') ||
    href.startsWith('https://') ||
    href.startsWith('mailto:') ||
    href.startsWith('tel:')
  );
}
</script>

<template>
  <!-- Minimal: title + intro (+ optional read more). Also when featured is chosen but image is missing. -->
  <div v-if="!showFeaturedBand" class="page-header">
    <h1
      v-if="hasTitleHtml"
      ref="richTitleHeadingEl"
      class="page-header__title page-header__title--rich block-rich-text"
      v-html="richTitleHeadingInnerHtml"
    />
    <h1 v-else class="page-header__title">{{ plainTitle }}</h1>
    <p v-if="description" class="page-header__description">{{ description }}</p>
    <a
      v-if="readMore && isExternalOrHash(readMore.href)"
      :href="readMore.href"
      class="page-header__read-more"
    >
      {{ readMore.text }}
      <ArrowRight class="page-header__read-more-arrow" aria-hidden="true" />
    </a>
    <RouterLink v-else-if="readMore" :to="readMore.href" class="page-header__read-more">
      {{ readMore.text }}
      <ArrowRight class="page-header__read-more-arrow" aria-hidden="true" />
    </RouterLink>
  </div>

  <!-- Featured (home): full-bleed gradient band + two-column layout on large screens -->
  <div v-else class="page-header-featured-breakout">
    <div class="page-header-featured__shell">
      <div class="page-header-featured__inner">
        <div class="page-header-featured__grid">
          <div class="page-header-featured__text">
            <h1
              v-if="hasTitleHtml"
              ref="richTitleHeadingEl"
              class="page-header-featured__title page-header-featured__title--rich block-rich-text"
              v-html="richTitleHeadingInnerHtml"
            />
            <h1 v-else class="page-header-featured__title">{{ plainTitle }}</h1>
            <p v-if="description" class="page-header-featured__description">{{ description }}</p>
            <a
              v-if="readMore && isExternalOrHash(readMore.href)"
              :href="readMore.href"
              class="page-header__read-more"
            >
              {{ readMore.text }}
              <ArrowRight class="page-header__read-more-arrow" aria-hidden="true" />
            </a>
            <RouterLink v-else-if="readMore" :to="readMore.href" class="page-header__read-more">
              {{ readMore.text }}
              <ArrowRight class="page-header__read-more-arrow" aria-hidden="true" />
            </RouterLink>
          </div>
          <div class="page-header-featured__image-wrap">
            <div class="page-header-featured__image-overlay" aria-hidden="true" />
            <img
              v-if="featuredHeroMediaBroken"
              :src="offlineMediaPlaceholderUrl()"
              :alt="resolvedImageAlt"
              class="page-header-featured__img"
              decoding="async"
              width="120"
              height="80"
            />
            <picture
              v-else-if="pageImage && isOptimisedImage(pageImage)"
              class="page-header-featured__picture"
            >
              <source
                v-for="source in pageImage.sources"
                :key="source.media_type"
                :type="source.media_type"
                :srcset="buildSrcSet(source.src_set)"
              />
              <img
                :src="pageImage.fallback!.url"
                :srcset="buildSrcSet(pageImage.fallback!.src_set)"
                sizes="(min-width: 1024px) 50vw, 100vw"
                :alt="resolvedImageAlt"
                class="page-header-featured__img"
                fetchpriority="high"
                decoding="async"
                @error="onFeaturedHeroMediaError"
              />
            </picture>
            <img
              v-else-if="pageImageUrl"
              :src="pageImageUrl"
              :alt="resolvedImageAlt"
              class="page-header-featured__img"
              fetchpriority="high"
              decoding="async"
              @error="onFeaturedHeroMediaError"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Minimal — matches Prototype Accreditation.tsx: wrapper pt-6 sm:pt-8 md:pt-12 + inner space-y-2,
   then space-y-6 before the next page section (margin-bottom here). */
.page-header {
  margin-top: 0;
  padding-top: 1.5rem;
  /* Spacing before page content: PageView .page-view__header-group margin-bottom (prototype py-8 / py-12). */
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
@media (min-width: 640px) {
  .page-header {
    padding-top: 2rem;
  }
}
@media (min-width: 768px) {
  .page-header {
    padding-top: 3rem;
  }
}
.page-header__title {
  margin: 0;
  color: var(--color-text);
  /* text-2xl md:text-3xl font-semibold */
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1.25;
}
@media (min-width: 768px) {
  .page-header__title {
    font-size: 1.875rem;
  }
}
.page-header__title--rich.block-rich-text :deep(.indmo-richtext-primary),
.page-header-featured__title--rich.block-rich-text :deep(.indmo-richtext-primary) {
  color: var(--color-primary);
  line-height: inherit;
}
.page-header__title--rich.block-rich-text :deep(.indmo-richtext-icon),
.page-header-featured__title--rich.block-rich-text :deep(.indmo-richtext-icon) {
  display: inline-flex;
  vertical-align: -0.125em;
  margin-inline-end: 0.15em;
  color: var(--color-primary);
}
.page-header__title--rich.block-rich-text :deep(.indmo-richtext-icon svg),
.page-header-featured__title--rich.block-rich-text :deep(.indmo-richtext-icon svg) {
  width: 1em;
  height: 1em;
}
.page-header__title--rich.block-rich-text :deep(p) {
  margin: 0;
}
.page-header-featured__title--rich.block-rich-text :deep(p) {
  margin: 0;
  line-height: inherit;
}
/* Plain text + inline markup inside h1 — same tight leading as ModernHero leading-[0.9] */
.page-header-featured__title--rich.block-rich-text :deep(span) {
  line-height: inherit;
}

.page-header__description {
  margin: 0;
  color: var(--color-text-muted);
  /* text-base md:text-lg font-normal */
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
}
@media (min-width: 768px) {
  .page-header__description {
    font-size: 1.125rem;
  }
}

/* Prototype ModernHero CTA: inline-flex gap-2 px-8 py-3 text-lg font-semibold rounded-sm shadow-lg
   hover:bg-button/90 hover:shadow-xl transition-all duration-200 */
.page-header__read-more {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  align-self: flex-start;
  margin-top: 0.25rem;
  width: fit-content;
  min-height: 55px;
  padding: 0.75rem 2rem;
  background: var(--color-button);
  color: var(--color-button-foreground);
  border-radius: var(--radius-button);
  font-weight: 600;
  font-size: 1.125rem;
  line-height: 1.25;
  text-decoration: none;
  box-shadow: var(--shadow-button);
  transition:
    background-color 200ms ease,
    box-shadow 200ms ease,
    transform 200ms ease;
}
.page-header__read-more:hover {
  background: color-mix(in srgb, var(--color-button) 90%, transparent);
  box-shadow: var(--shadow-button-hover);
  transform: translateY(-2px);
}
.page-header__read-more-arrow {
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
  transition: transform 200ms ease;
}
.page-header__read-more:hover .page-header__read-more-arrow {
  transform: translateX(4px);
}

/* Full-bleed breakout inside .app-main-inner (prototype ModernHero).
   No margin-top — sit flush below the fixed Navigation (main padding-top clears the nav). */
.page-header-featured-breakout {
  margin-top: 0;
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
}

.page-header-featured__shell {
  background: linear-gradient(
    to bottom right,
    rgba(4, 120, 87, 0.12),
    #ffffff,
    rgba(4, 120, 87, 0.18)
  );
  overflow: hidden;
  border-bottom: 1px solid var(--color-border);
}

.page-header-featured__inner {
  width: 100%;
  max-width: 80rem;
  margin-left: auto;
  margin-right: auto;
  padding-left: 1rem;
  padding-right: 1rem;
  box-sizing: border-box;
}
@media (min-width: 640px) {
  .page-header-featured__inner {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
}
@media (min-width: 1024px) {
  .page-header-featured__inner {
    padding-left: 2rem;
    padding-right: 2rem;
  }
}

.page-header-featured__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
}
/* Prototype ModernHero: grid lg:grid-cols-2 gap-8 lg:gap-12 — equal text / image split */
@media (min-width: 1024px) {
  .page-header-featured__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-items: center;
    column-gap: 3rem;
  }
}

.page-header-featured__text {
  padding: 2rem 0 1.5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0;
}
@media (min-width: 640px) {
  .page-header-featured__text {
    /* Prototype ModernHero: py-8 sm:py-12 */
    padding: 3rem 0 2rem;
  }
}
@media (min-width: 1024px) {
  .page-header-featured__text {
    /* lg:py-16 (ModernHero text column) */
    padding: 4rem 0;
  }
}

/* Prototype ModernHero h1: text-[2rem] sm:text-[2.88rem] lg:text-[3.6rem] font-bold leading-[0.9].
   Vertical stack uses space-y-6 → 1.5rem below title before description / next block. */
.page-header-featured__title {
  margin: 0 0 1.5rem;
  color: var(--color-text);
  font-size: 2.25rem;
  font-weight: 700;
  line-height: 0.95;
}
@media (min-width: 640px) {
  .page-header-featured__title {
    font-size: 3.25rem;
  }
}
@media (min-width: 1024px) {
  .page-header-featured__title {
    font-size: 4rem;
  }
}

/* Prototype ModernHero: text-[1.05rem] sm:text-xl text-muted-foreground leading-relaxed max-w-xl */
.page-header-featured__description {
  margin: 0;
  max-width: 36rem;
  color: var(--color-text-muted);
  font-size: 1.05rem;
  font-weight: 400;
  line-height: 1.625;
}
@media (min-width: 640px) {
  .page-header-featured__description {
    font-size: 1.25rem;
  }
}

.page-header-featured__text .page-header__read-more {
  margin-top: 1.5rem;
}

/* Prototype ModernHero image: h-[400px] default, lg:h-[600px] — shell height follows grid row */
.page-header-featured__image-wrap {
  position: relative;
  margin-left: -1rem;
  margin-right: -1rem;
  width: calc(100% + 2rem);
  height: 25rem;
}
@media (min-width: 640px) {
  .page-header-featured__image-wrap {
    margin-left: -1.5rem;
    margin-right: -1.5rem;
    width: calc(100% + 3rem);
    height: 25rem;
  }
}
@media (min-width: 1024px) {
  .page-header-featured__image-wrap {
    margin-left: 0;
    margin-right: calc(-1 * max(0px, (100vw - 80rem) / 2 + 2rem));
    width: auto;
    height: 37.5rem;
  }
}

/* Prototype ModernHero: bg-gradient-to-br from-primary/30 to-transparent */
.page-header-featured__image-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom right,
    rgba(4, 120, 87, 0.3),
    transparent
  );
  z-index: 10;
  pointer-events: none;
}

.page-header-featured__picture,
.page-header-featured__img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
</style>
